/** Iter 104 — vehicle expense manager: list, add (+attachment), edit w/ audit, void. */
import { useEffect, useState, useCallback } from "react";
import api, { API, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import Money from "@/components/Money";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Plus, Paperclip, Pencil, Trash2, Loader2 } from "lucide-react";

export const CATEGORY_LABELS = {
  fuel: "Fuel", maintenance: "Maintenance", oil_service: "Oil / Service", tires: "Tires",
  repairs: "Repairs", accident_repairs: "Accident repairs", bodywork: "Bodywork",
  cleaning: "Cleaning", insurance: "Insurance", registration: "Registration",
  road_tax: "Road tax", technical_inspection: "Technical inspection", parking: "Parking",
  fines: "Fines", towing: "Towing", replacement_parts: "Replacement parts",
  loan_lease: "Vehicle loan / lease", depreciation: "Depreciation",
  staff_driver: "Staff / driver", delivery_collection: "Delivery / collection", other: "Other",
  battery: "Battery", registration_tax: "Registration / Road tax", repair: "Repair",
  inspection: "Inspection (ITP/MOT)", fine: "Fine",
};

const PAY_METHODS = ["Cash", "Card", "Bank transfer", "Other"];
const EMPTY = { car_id: "", date: new Date().toISOString().slice(0, 10), category: "fuel", amount: "", vat_amount: "", description: "", supplier: "", payment_method: "Cash", reference: "", notes: "" };

export default function VehicleExpensesPanel({ cars, dateFrom, dateTo, carFilter, onChanged }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (carFilter) p.set("car_id", carFilter);
    if (dateFrom) p.set("date_from", dateFrom);
    if (dateTo) p.set("date_to", dateTo);
    api.get(`/vendor/reports/expenses?${p}`).then((r) => setRows(r.data.items || [])).catch(() => {});
  }, [carFilter, dateFrom, dateTo]);
  useEffect(() => { load(); }, [load]);

  const carLabel = (id) => {
    const c = (cars || []).find((x) => x.id === id);
    return c ? `${c.make} ${c.model}${c.license_plate ? ` · ${c.license_plate}` : ""}` : "—";
  };

  const openCreate = () => {
    setEditing(null);
    setDraft({ ...EMPTY, car_id: carFilter || cars?.[0]?.id || "" });
    setFile(null);
    setOpen(true);
  };

  const openEdit = (e) => {
    setEditing(e);
    setDraft({ car_id: e.car_id, date: e.date, category: e.category, amount: String(e.amount),
      vat_amount: e.vat_amount ? String(e.vat_amount) : "", description: e.description || "",
      supplier: e.supplier || "", payment_method: e.payment_method || "Cash",
      reference: e.reference || "", notes: e.notes || "" });
    setFile(null);
    setOpen(true);
  };

  const save = async () => {
    if (!(Number(draft.amount) > 0)) { toast.error("Enter a valid amount"); return; }
    setSaving(true);
    try {
      if (editing) {
        const reason = window.prompt("Reason for this change (required, kept in the audit log)");
        if (!reason) { setSaving(false); return; }
        await api.put(`/vendor/reports/expenses/${editing.id}`, {
          date: draft.date, category: draft.category, amount: Number(draft.amount),
          vat_amount: Number(draft.vat_amount || 0), description: draft.description || null,
          supplier: draft.supplier || null, payment_method: draft.payment_method || null,
          reference: draft.reference || null, notes: draft.notes || null, reason,
        });
        toast.success("Expense updated — audit kept");
      } else {
        const body = { ...draft, amount: Number(draft.amount), vat_amount: Number(draft.vat_amount || 0) };
        if (file) {
          const data = await new Promise((res, rej) => {
            const r = new FileReader();
            r.onload = () => res(r.result);
            r.onerror = rej;
            r.readAsDataURL(file);
          });
          body.attachment_name = file.name;
          body.attachment_data = data;
        }
        await api.post("/vendor/reports/expenses", body);
        toast.success("Expense recorded");
      }
      setOpen(false);
      load();
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  const voidExpense = async (e) => {
    const reason = window.prompt("Reason for voiding this expense (kept in history)");
    if (!reason) return;
    try {
      await api.post(`/vendor/reports/expenses/${e.id}/void?reason=${encodeURIComponent(reason)}`);
      toast.success("Expense voided");
      load();
      onChanged?.();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="rounded-2xl border border-border bg-white p-4" data-testid="vehicle-expenses-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div>
          <div className="font-bold">Vehicle expenses</div>
          <div className="text-xs text-neutral-500">Fuel, maintenance, insurance… every cost recorded against a vehicle.</div>
        </div>
        <Button onClick={openCreate} className="rounded-full" size="sm" data-testid="add-expense-btn">
          <Plus className="h-4 w-4 mr-1" /> Add vehicle expense
        </Button>
      </div>
      {rows.length === 0 ? (
        <div className="text-sm text-neutral-400 text-center py-6" data-testid="expenses-empty">No expenses in this period.</div>
      ) : (
        <div className="space-y-1.5">
          {rows.map((e) => (
            <div key={e.id} className="flex items-center justify-between gap-3 py-2 border-b border-dashed border-border last:border-0" data-testid={`expense-row-${e.id}`}>
              <div className="min-w-0">
                <div className="text-sm font-semibold truncate">
                  {CATEGORY_LABELS[e.category] || e.category}
                  {e.description ? <span className="font-normal text-neutral-500"> — {e.description}</span> : ""}
                </div>
                <div className="text-[11px] text-neutral-400 truncate">
                  {e.date} · {carLabel(e.car_id)}{e.supplier ? ` · ${e.supplier}` : ""}
                  {(e.edits || []).length > 0 && ` · ${e.edits.length} correction${e.edits.length > 1 ? "s" : ""}`}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {e.has_attachment && (
                  <a href={`${API}/vendor/reports/expenses/${e.id}/attachment`} target="_blank" rel="noreferrer" className="text-neutral-500 hover:text-blue-600" title={e.attachment_filename || "Receipt"} data-testid={`expense-attachment-${e.id}`}>
                    <Paperclip className="h-3.5 w-3.5" />
                  </a>
                )}
                <span className="text-sm font-bold"><Money amount={Number(e.amount) + Number(e.vat_amount || 0)} /></span>
                <button type="button" onClick={() => openEdit(e)} className="text-blue-600" data-testid={`edit-expense-${e.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                <button type="button" onClick={() => voidExpense(e)} className="text-rose-500" data-testid={`void-expense-${e.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={(v) => { if (!v) setOpen(false); }}>
        <DialogContent className="max-w-lg" data-testid="expense-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit expense" : "Add vehicle expense"}</DialogTitle>
            <DialogDescription>{editing ? "Every change is kept in the audit trail." : "The expense appears immediately in the financial report."}</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Vehicle</Label>
              <select value={draft.car_id} disabled={!!editing} onChange={(e) => setDraft({ ...draft, car_id: e.target.value })} className="mt-1 w-full rounded-lg border border-border px-2 py-2 text-sm" data-testid="expense-car">
                {(cars || []).map((c) => <option key={c.id} value={c.id}>{c.make} {c.model}{c.license_plate ? ` · ${c.license_plate}` : ""}</option>)}
              </select>
            </div>
            <div>
              <Label>Date</Label>
              <Input type="date" value={draft.date} onChange={(e) => setDraft({ ...draft, date: e.target.value })} className="mt-1" data-testid="expense-date" />
            </div>
            <div>
              <Label>Category</Label>
              <select value={draft.category} onChange={(e) => setDraft({ ...draft, category: e.target.value })} className="mt-1 w-full rounded-lg border border-border px-2 py-2 text-sm" data-testid="expense-category">
                {Object.entries(CATEGORY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div>
              <Label>Amount (€)</Label>
              <Input type="number" inputMode="decimal" value={draft.amount} onChange={(e) => setDraft({ ...draft, amount: e.target.value })} className="mt-1" data-testid="expense-amount" />
            </div>
            <div>
              <Label>VAT (€, optional)</Label>
              <Input type="number" inputMode="decimal" value={draft.vat_amount} onChange={(e) => setDraft({ ...draft, vat_amount: e.target.value })} className="mt-1" data-testid="expense-vat" />
            </div>
            <div>
              <Label>Supplier</Label>
              <Input value={draft.supplier} onChange={(e) => setDraft({ ...draft, supplier: e.target.value })} className="mt-1" data-testid="expense-supplier" />
            </div>
            <div>
              <Label>Payment method</Label>
              <select value={draft.payment_method} onChange={(e) => setDraft({ ...draft, payment_method: e.target.value })} className="mt-1 w-full rounded-lg border border-border px-2 py-2 text-sm" data-testid="expense-method">
                {PAY_METHODS.map((m) => <option key={m}>{m}</option>)}
              </select>
            </div>
            <div className="col-span-2">
              <Label>Description</Label>
              <Input value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} className="mt-1" data-testid="expense-description" />
            </div>
            <div>
              <Label>Invoice / reference</Label>
              <Input value={draft.reference} onChange={(e) => setDraft({ ...draft, reference: e.target.value })} className="mt-1" data-testid="expense-reference" />
            </div>
            <div>
              <Label>Notes</Label>
              <Input value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} className="mt-1" data-testid="expense-notes" />
            </div>
            {!editing && (
              <div className="col-span-2">
                <Label>Receipt / invoice (PDF, JPG, PNG — max 4MB)</Label>
                <input type="file" accept="application/pdf,image/jpeg,image/png" onChange={(e) => setFile(e.target.files?.[0] || null)} className="mt-1 block w-full text-xs" data-testid="expense-file" />
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="expense-save-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
              {editing ? "Save changes" : "Add expense"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
